import { AxiosError } from 'axios'
import { AuthModel } from './_models'
import Swal from 'sweetalert2'
import { LOGIN_URL } from './_requests'
import { handleAPIErrors, hasErrorText } from '../../../common/ErrorHandler'
import { FORBIDDEN_TEXT, getForbiddenReason, getGeneralError, SERVICE_UNAVAILABLE_TEXT } from '../../../common/api/errors'
import { isDisputeMutationUrl } from './disputeMutationUrl'
import { isSessionExpired } from './sessionExpiry'
import { showsItsOwnErrors } from './ownErrorUrls'
import { rendersErrorInline } from './inlineErrorUrls'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const getAuth = (): AuthModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      // You can easily check auth_token expiration also
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

/**
 * Text of the common dialog for a failed request other than 400: the backend's sentence when the body
 * carries one, the axios text otherwise, both in the same Russian frame; a fixed text for an unreachable
 * dependency.
 */
const serverErrorText = (response: AxiosError['response'], axiosMessage: string): string => {
  if (response?.status === 503) {
    return SERVICE_UNAVAILABLE_TEXT
  }

  return 'Произошла ошибка на сервере: ' + (getGeneralError(response) ?? axiosMessage)
}

/** A 400 without a backend sentence (a proxy page, a binary body). */
const GENERIC_400_TEXT = 'Произошла ошибка на сервере, повторите позже'

/**
 * Whether a string body is a page (a proxy's HTML refusal) rather than a sentence: its markup means nothing
 * to the user, so the common dialog shows the generic text instead. A body declared `text/plain` is a
 * sentence whatever its first character.
 */
const isMarkupBody = (response: AxiosError['response'], body: string): boolean => {
  const contentType = String((response?.headers as Record<string, unknown> | undefined)?.['content-type'] ?? '')

  return !contentType.toLowerCase().startsWith('text/plain') && body.trim().startsWith('<')
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'

  axios.interceptors.request.use(
    (config: { headers: { 'x-api-key': string } }) => {
      const auth = getAuth()

      if (auth && auth.auth_token) {
        config.headers['x-api-key'] = auth.auth_token
      }

      return config
    },
    (error: any) => Promise.reject(error)
  )

  axios.interceptors.response.use(
    (response: any) => response,
    (error: AxiosError) => {
      if (!error.response) {
        Swal.fire({ title: 'Ошибка сети', text: 'Не удалось подключиться к серверу', icon: 'error' })
        return Promise.reject(error)
      }

      const status = error.response?.status
      const url = error.config.url
      const method = error.config.method

      if (status === 403 && !showsItsOwnErrors(url, LOGIN_URL) && !rendersErrorInline(url, method)) {
        // Backend text goes in as text, never as HTML.
        Swal.fire({
          title: FORBIDDEN_TEXT,
          text: getForbiddenReason(error.response) ?? '',
          icon: 'warning',
        })
      }

      if (isSessionExpired(status, error.response?.data)) {
        removeAuth()

        if (window.location.pathname !== '/auth') {
          window.location.reload()
        }
      }

      if (
        // Аудит интеграций показывает текст прокси («не ответил за 5 секунд»), а не код ответа.
        !url?.includes('/internal/audit/') &&
        status !== 400 &&
        // 403 already has its own warning above; a second dialog would replace it.
        status !== 403 &&
        !isDisputeMutationUrl(url) &&
        // The credited amount change window shows its own text.
        !url?.includes('/credited-amount') &&
        // Справочники причин показывают свой текст на 409 REORDER_SET_MISMATCH; без исключения
        // сверху ложится техническое «Request failed with status code 409».
        !url?.includes('-reason/reorder') &&
        // Create/edit forms and dialogs that render the refusal themselves (see inlineErrorUrls).
        !rendersErrorInline(url, method) &&
        // Вход, страница учётной записи, белый список IP и второй фактор сотрудников показывают
        // свой текст ошибки на месте.
        !showsItsOwnErrors(url, LOGIN_URL) &&
        // Настройки системы показывают текст ошибки на самой странице — в таблице и под
        // полем; общий алерт поверх него сообщал бы только код ответа.
        !url?.includes('/internal/settings') &&
        !url?.includes('/internal/payment-adapter/') &&
        !url?.includes('/internal/matching/profiles') &&
        // The concierge window shows its own refusal text.
        !url?.includes('/concierge') &&
        !url?.includes('/ledger-wallet-balance') &&
        // Подсказка со снимком клиента открывается наведением и пишет свою ошибку внутри себя.
        !url?.includes('/client-snapshot/') &&
        !url?.includes('/ledger-deposits/') &&
        !url?.includes('/balance-policy') &&
        !url?.includes('/logs/risk-score-changes') &&
        // Карточка тикета показывает свой текст ошибки на самой странице («Тикет не найден»);
        // общий алерт поверх него сообщал бы только код ответа.
        !url?.includes('/internal/ticket/') &&
        // Списки и карточки отказов показывают свой текст («Отказ не найден») на странице.
        !url?.includes('/internal/payout-refusal') &&
        !url?.includes('/internal/matching/payout-refusal')
      ) {
        Swal.fire({ title: 'Ошибка', text: serverErrorText(error.response, error.message), icon: 'error' })
      }

      if (
        !url?.includes('/internal/audit/') &&
        status === 400 &&
        !isDisputeMutationUrl(url) &&
        !url?.includes('/credited-amount') &&
        !url?.includes('-reason/reorder') &&
        // Create/edit forms and dialogs that render the refusal themselves (see inlineErrorUrls).
        !rendersErrorInline(url, method) &&
        // Вход, страница учётной записи, белый список IP и второй фактор сотрудников показывают
        // свой текст ошибки на месте.
        !showsItsOwnErrors(url, LOGIN_URL) &&
        !url?.includes('/internal/settings') &&
        !url?.includes('/concierge') &&
        !url?.includes('/ledger-wallet-balance') &&
        // Подсказка со снимком клиента открывается наведением и пишет свою ошибку внутри себя.
        !url?.includes('/client-snapshot/') &&
        !url?.includes('/ledger-deposits/') &&
        !url?.includes('/balance-policy') &&
        !url?.includes('/logs/risk-score-changes') &&
        !url?.includes('/internal/ticket/') &&
        !url?.includes('/internal/payout-refusal') &&
        !url?.includes('/internal/matching/payout-refusal')
      ) {
        const data = error.response?.data
        const errors = (data as { errors?: unknown } | null | undefined)?.errors

        if (typeof data === 'string' && data.trim() !== '' && !isMarkupBody(error.response, data)) {
          // A plain-text 400 (a framework or proxy refusal) is the reason itself.
          Swal.fire({ title: 'Ошибка', text: data, icon: 'error' })
        } else if (hasErrorText(errors)) {
          handleAPIErrors(errors, status)
        } else if (errors && typeof errors === 'object') {
          // Legacy `errors: []` / `errors: {}` of a controller not migrated yet: silent, as before — the page
          // shows its own text.
        } else {
          // A 400 that did not come from our backend (proxy page, a Blob body of a file request) has no
          // `errors` object and no sentence for the user: the generic Russian text, not the axios English one.
          Swal.fire({ title: 'Ошибка', text: GENERIC_400_TEXT, icon: 'error' })
        }
      }

      return Promise.reject(error.response)
    }
  )
}

export { getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY }
